import axios from "axios";
import config from "../config/index";
import authService from "../features/auth/authService";

// Create axios instance
const api = axios.create({
  baseURL: config.apiURL,
});

// Store a reference to the logout function
let logoutFunction = null;

// Function to set the logout function from AuthContext
export const setLogoutFunction = (logoutFn) => {
  logoutFunction = logoutFn;
};

// Store a reference to get the current season
let getCurrentSeason = () => null;

// Function to set the current season getter from SeasonContext
export const setCurrentSeasonGetter = (getter) => {
  getCurrentSeason = getter;
};

// Request interceptor to add auth token and season parameter
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await authService.getCurrentUserToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn("Failed to get auth token:", error);
      // Continue with request even if token fetch fails
    }

    // AUTO-ADD SEASON PARAMETER to all requests
    // Only add if season isn't already specified
    const season = getCurrentSeason();
    if (season && !config.params?.season) {
      config.params = {
        ...config.params,
        season: season,
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 || error.response?.status === 403) {
      // Don't retry public endpoints that don't require auth
      if (originalRequest.url?.includes("/seasons")) {
        return Promise.reject(error);
      }

      // Prevent infinite loops
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      console.error("Authentication failed - token expired or invalid");

      originalRequest._retry = true;
      try {
        const token = await authService.getCurrentUserToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } else {
          // Only logout if there's actually a user session to clear
          if (logoutFunction) {
            await logoutFunction();
          }
        }
      } catch (tokenError) {
        console.error("Failed to refresh token:", tokenError);
        if (logoutFunction) {
          await logoutFunction();
        }
      }
    }

    return Promise.reject(error);
  },
);

export default api;
